/**
 * Theme Context - React context for managing theme state
 *
 * Provides:
 * - Current mode (light/dark) and theme (company/materiality) state
 * - toggleMode() to switch between light/dark
 * - setTheme() to change brand theme
 * - Auto-detection of system color scheme preference
 * - Auto-detection of app theme based on route (Materiality Tracker vs Company Tracker)
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode, useRef } from 'react';
import {
  ThemeMode,
  ThemeBrand,
  getMode,
  setMode as setModeStorage,
  toggleMode as toggleModeStorage,
  getTheme,
  setTheme as setThemeStorage,
  initializeTheme,
} from '@features/theme/utils/theme-manager';
import { useAppSelector } from '@reducers/index';
import config from '../../../config';

const MATERIALITY_TRACKER_PATH_PREFIX = '/materiality-tracker';

interface ThemeContextValue {
  mode: ThemeMode;
  theme: ThemeBrand;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  setTheme: (theme: ThemeBrand) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const noop = () => {};
const DISABLED_THEME_VALUE: ThemeContextValue = {
  mode: 'light',
  theme: 'company',
  toggleMode: noop,
  setMode: noop,
  setTheme: noop,
  isDarkMode: false,
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const isLoggedIn = useAppSelector((state) => state.currentUser.loaded);
  const isThemeEnabled = config.features.darkMode && isLoggedIn;

  const [mode, setModeState] = useState<ThemeMode>(() => getMode());
  const [theme, setThemeState] = useState<ThemeBrand>(() => getTheme());

  useEffect(() => {
    if (isThemeEnabled) {
      initializeTheme();
    } else {
      document.documentElement.removeAttribute('data-mode');
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isThemeEnabled]);

  const toggleMode = useCallback(() => {
    const newMode = toggleModeStorage();
    setModeState(newMode);
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeStorage(newMode);
    setModeState(newMode);
  }, []);

  const setTheme = useCallback((newTheme: ThemeBrand) => {
    setThemeStorage(newTheme);
    setThemeState(newTheme);
  }, []);

  const enabledValue = useMemo<ThemeContextValue>(
    () => ({
      mode,
      theme,
      toggleMode,
      setMode,
      setTheme,
      isDarkMode: mode === 'dark',
    }),
    [mode, theme, toggleMode, setMode, setTheme],
  );

  return (
    <ThemeContext.Provider value={isThemeEnabled ? enabledValue : DISABLED_THEME_VALUE}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useThemeMode = (): Pick<ThemeContextValue, 'mode' | 'toggleMode' | 'setMode' | 'isDarkMode'> => {
  const { mode, toggleMode, setMode, isDarkMode } = useTheme();
  return { mode, toggleMode, setMode, isDarkMode };
};

export const useThemeBrand = (): Pick<ThemeContextValue, 'theme' | 'setTheme'> => {
  const { theme, setTheme } = useTheme();
  return { theme, setTheme };
};

/**
 * Detects the appropriate theme based on the current route path.
 * Returns 'materiality' for Materiality Tracker routes, 'company' otherwise.
 */
const detectThemeFromPath = (pathname: string): ThemeBrand => {
  return pathname.startsWith(MATERIALITY_TRACKER_PATH_PREFIX) ? 'materiality' : 'company';
};

/**
 * Hook to automatically sync theme based on route changes.
 * Should be used inside a component that has access to route location.
 *
 * @param pathname - Current route pathname (from useLocation or similar)
 */
const useAutoThemeDetection = (pathname: string): void => {
  const { setTheme, theme } = useTheme();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return;
    }
    prevPathnameRef.current = pathname;

    const detectedTheme = detectThemeFromPath(pathname);
    if (detectedTheme !== theme) {
      setTheme(detectedTheme);
    }
  }, [pathname, setTheme, theme]);
};

/**
 * Component that auto-detects and applies theme based on current route.
 * Place this inside your router to enable automatic theme switching.
 */
interface ThemeRouteDetectorProps {
  pathname: string;
}

export const ThemeRouteDetector = ({ pathname }: ThemeRouteDetectorProps): null => {
  useAutoThemeDetection(pathname);
  return null;
};
